import {
  ChangeEvent,
  FormEvent,
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Modal } from "rsuite";

import FeedbackMediaPreview from "../../views/supportal/FeedbackView/FeedbackMediaPreview";
import UppyFile, { UppyFileInterface } from "../Inputs/UppyFile";
import useClickOutside from "../../hooks/useClickOutside";
import AppSpinner from "../../elements/AppSpinner";
import { callFunctionIfExists } from "../../utils/GeneralUtils";

interface Props {
  disableInput?: boolean;
  inputText: string;
  attachments?: UppyFileInterface[];
  onSend: (text: string) => void;
  onUseVoice?: () => void;
  onMessageChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onUpload?: (files: UppyFileInterface[]) => void;
  removeAttachment?: (file: UppyFileInterface) => void;
  sendMessage: (e: FormEvent | undefined) => void;
}

const BrieChatInput = (
  {
    disableInput,
    inputText,
    attachments,
    onUseVoice,
    onMessageChange,
    onUpload,
    removeAttachment,
    sendMessage,
  }: Props,
  ref: Ref<any>
) => {
  const [modalopen, setModalOpen] = useState(false);
  const [focus, setFocus] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const uppyRef = useRef<any>(null);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        textareaRef?.current?.focus();
        setFocus(true);
      },
    }),
    [textareaRef.current]
  );

  useClickOutside(inputRef, () => setFocus(false));

  useEffect(() => {
    if (!attachments || !onUpload || !removeAttachment) return;
    if (!inputRef.current) return;
    inputRef.current.addEventListener("paste", pasteImage);
    return () => {
      if (!inputRef.current) return;
      inputRef.current.removeEventListener("paste", pasteImage);
    };
  }, [
    inputRef.current,
    uppyRef.current,
    attachments,
    onUpload,
    removeAttachment,
  ]);

  return (
    <>
      <form
        className="tw:flex tw:mx-3 tw:pt-2 tw:items-center"
        onSubmit={sendMessage}
      >
        {onUseVoice && (
          <i
            onClick={onUseVoice}
            style={{
              fontSize: "25px",
              lineHeight: 0.8,
              color: "var(--primary)",
            }}
            className="mdi mdi-microphone cursor-pointer tw:mr-1"
          />
        )}
        <div
          ref={inputRef}
          style={{
            border: onUpload
              ? `2.5px solid ${focus ? "var(--primary)" : "#e0e0e0"}`
              : undefined,
            borderRadius: onUpload ? 4 : undefined,
            maxHeight: onUpload ? 58 : 40,
          }}
          onMouseDown={() => setFocus(true)}
          className="tw:w-full tw:flex tw:items-center "
        >
          {uploading && (
            <div
              style={{
                width: 24,
                height: 24,
                marginTop: -5,
                marginLeft: 5,
              }}
            >
              <AppSpinner color="primary" size="sm" />
            </div>
          )}
          {attachments && removeAttachment && (
            <div className="ml-1">
              {attachments &&
                attachments?.length > 0 &&
                attachments.map((file) => (
                  <FeedbackMediaPreview
                    key={file.id}
                    removeFile={() => removeAttachment(file)}
                    file={file}
                  />
                ))}
            </div>
          )}
          {onUpload ? (
            <textarea
              ref={textareaRef}
              className="tw:border-none tw:outline-none tw:resize-none tw:bg-transparent tw:w-full tw:ml-1"
              rows={1}
              onChange={onMessageChange}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(undefined);
                }
              }}
              value={inputText}
              disabled={disableInput}
            />
          ) : (
            <input
              className="form-control form-control-sm"
              // @ts-ignore
              onChange={onMessageChange}
              value={inputText}
              disabled={disableInput}
              // @ts-ignore
              ref={textareaRef}
            />
          )}
          {attachments && onUpload && removeAttachment && (
            <i
              className="mdi mdi-image-area text-primary tw:mr-1 cursor-pointer"
              style={{ fontSize: 24 }}
              onClick={() => setModalOpen(true)}
            />
          )}
        </div>
        <i className="mdi mdi-send tw:pl-2 brie-send" onClick={sendMessage} />
        <UppyFile
          ref={uppyRef}
          hidden
          multiFiles
          endpoint="/feedback/file"
          meta={{ isPublic: true }}
          plugins={["OneDrive", "GoogleDrive", "Box", "ScreenCapture"]}
          fileType="Media"
          filesToFilter={attachments ?? []}
          afterSubmit={(files) => {
            callFunctionIfExists(
              onUpload,
              Array.isArray(files) ? files : [files]
            );
          }}
        />
        {onUpload && (
          <Modal
            open={modalopen}
            onClose={() => setModalOpen(false)}
            size="xs"
            backdrop="static"
          >
            <Modal.Header>
              <Modal.Title className="tw:pt-3 tw:pl-3 h2">
                Upload Image
              </Modal.Title>
            </Modal.Header>
            <div className="tw:p-4 tw:w-full tw:flex tw:justify-center">
              <UppyFile
                multiFiles
                endpoint="/feedback/file"
                meta={{ isPublic: true }}
                plugins={["OneDrive", "GoogleDrive", "Box", "ScreenCapture"]}
                fileType="Media"
                afterSubmit={(files) => {
                  onUpload(Array.isArray(files) ? files : [files]);
                  setModalOpen(false);
                }}
                filesToFilter={attachments ?? []}
              />
            </div>
          </Modal>
        )}
      </form>
    </>
  );

  async function pasteImage(e: ClipboardEvent) {
    setUploading(true);
    const files = e.clipboardData?.files;

    if (!files || !files.length) return setUploading(false);

    e.preventDefault();
    if (attachments && attachments.length > 0) return setUploading(false);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      uppyRef.current?.addFile(file);
    }

    if (uppyRef.current.getFiles().length > 0) {
      await uppyRef.current?.uploadFiles();
      setUploading(false);
    }
  }
};

export default forwardRef(BrieChatInput);
